import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { formatCurrency, formatDateTime } from "../lib/utils";
import USDHint from "../components/USDHint";
import toast from "react-hot-toast";
import axios from "axios";
import { 
  AlertTriangle, 
  Clock, 
  Search,
  Calendar,
  DollarSign,
  ChevronDown,
  ChevronUp,
  Truck,
  CheckCircle,
  MessageCircle
} from "lucide-react";

const API = `${window.location.origin}/api`;

export default function SupplierDebtsPage() {
  const { } = useAuth();
  const language = "ar";
  const { t } = useTranslation(language);
  const [purchases, setPurchases] = useState([]);
  const [settings, setSettings] = useState({});
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, suppliersRes, settingsRes] = await Promise.all([
        axios.get(`${API}/purchases`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/settings`),
      ]);
      
      // فقط المشتريات بالدين التي لم تسدد بالكامل
      const creditPurchases = purchasesRes.data.filter(
        p => p.payment_type === "credit" && (p.remaining > 0 || p.total - (p.amount_paid || 0) > 0)
      );
      setPurchases(creditPurchases);
      setSuppliers(suppliersRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  const getSupplierPhone = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier?.phone || "";
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (dueDate, remaining) => {
    if (remaining <= 0) {
      return { color: "bg-green-500/20 text-green-400", text: language === "ar" ? "مسدد" : "Paid", icon: CheckCircle };
    }
    
    const days = getDaysUntilDue(dueDate);
    if (days === null) {
      return { color: "bg-slate-500/20 text-slate-400", text: language === "ar" ? "بدون موعد" : "No due date", icon: Clock };
    }
    if (days < 0) {
      return { color: "bg-red-500/20 text-red-400", text: language === "ar" ? `متأخر ${Math.abs(days)} يوم` : `${Math.abs(days)} days overdue`, icon: AlertTriangle };
    }
    if (days <= 3) {
      return { color: "bg-amber-500/20 text-amber-400", text: language === "ar" ? `${days} أيام` : `${days} days left`, icon: AlertTriangle };
    }
    return { color: "bg-blue-500/20 text-blue-400", text: language === "ar" ? `${days} يوم` : `${days} days`, icon: Calendar };
  };

  const sendWhatsAppReminder = (purchase) => {
    const phone = getSupplierPhone(purchase.supplier_id);
    if (!phone) {
      toast.error(language === "ar" ? "لا يوجد رقم هاتف للمورد" : "No phone number for supplier");
      return;
    }

    const remaining = purchase.remaining || (purchase.total - (purchase.amount_paid || 0));
    const shopName = settings.shop_name || (language === "ar" ? "المتجر" : "Store");
    const dueDate = purchase.due_date ? new Date(purchase.due_date).toLocaleDateString(language === "ar" ? "ar-IQ" : "en-US") : "";
    
    const message = language === "ar" 
      ? `السلام عليكم،\n\nنود إبلاغكم بأن لدينا مبلغ مستحق لكم: ${formatCurrency(remaining)}\n${dueDate ? `تاريخ الاستحقاق: ${dueDate}\n` : ""}\nنحن على استعداد لتسديد المبلغ في أقرب وقت.\n\nشكراً لتعاملكم معنا\n${shopName}`
      : `Hello,\n\nThis is to inform you that we have an outstanding payment: ${formatCurrency(remaining)}\n${dueDate ? `Due date: ${dueDate}\n` : ""}\nWe are ready to settle the amount soon.\n\nThank you for your business\n${shopName}`;

    const cleanPhone = phone.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const toggleRowExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const filteredPurchases = purchases.filter(purchase => {
    const remaining = purchase.remaining || (purchase.total - (purchase.amount_paid || 0));
    const days = getDaysUntilDue(purchase.due_date);
    
    if (filterStatus === "overdue" && (days === null || days >= 0)) return false;
    if (filterStatus === "upcoming" && (days === null || days < 0 || days > 7)) return false;
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return purchase.supplier_name?.toLowerCase().includes(search);
    }
    return true;
  });

  const totalDebt = purchases.reduce((sum, p) => sum + (p.remaining || (p.total - (p.amount_paid || 0))), 0);
  const overdueCount = purchases.filter(p => {
    const days = getDaysUntilDue(p.due_date);
    return days !== null && days < 0;
  }).length;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="supplier-debts-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">
            {language === "ar" ? "الديون للموردين" : "Supplier Debts"}
          </h1>
          <p className="text-slate-400 mt-1">
            {language === "ar" ? "المبالغ المستحقة للموردين" : "Amounts owed to suppliers"}
          </p>
        </div>
        
        {/* Summary Cards */}
        <div className="flex gap-4">
          <Card className="bg-orange-500/10 border-orange-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <DollarSign className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-xs text-slate-400">{language === "ar" ? "إجمالي المستحقات" : "Total Owed"}</p>
                <p className="text-xl font-bold text-orange-500">{formatCurrency(totalDebt)}</p>
                <USDHint amount={totalDebt} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-xs text-slate-400">{language === "ar" ? "متأخرة" : "Overdue"}</p>
                <p className="text-xl font-bold text-red-500">{overdueCount}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400`} />
              <Input
                placeholder={language === "ar" ? "بحث باسم المورد..." : "Search by supplier name..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${language === "ar" ? "pr-10" : "pl-10"} bg-slate-900 border-slate-700 text-white`}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "all", label: language === "ar" ? "الكل" : "All" },
                { value: "overdue", label: language === "ar" ? "متأخرة" : "Overdue" },
                { value: "upcoming", label: language === "ar" ? "قريبة" : "Upcoming" },
              ].map(filter => (
                <Button
                  key={filter.value}
                  variant={filterStatus === filter.value ? "default" : "outline"}
                  onClick={() => setFilterStatus(filter.value)}
                  className={filterStatus === filter.value 
                    ? "bg-red-600 hover:bg-red-700" 
                    : "border-slate-600 text-slate-300 hover:bg-slate-700"}
                  size="sm"
                >
                  {filter.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Debts Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
            </div>
          ) : filteredPurchases.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
              <p className="text-slate-400">
                {language === "ar" ? "لا توجد مستحقات للموردين" : "No outstanding supplier debts"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header border-slate-700">
                    <TableHead className="text-amber-500 w-10"></TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "المورد" : "Supplier"}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "الهاتف" : "Phone"}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "المبلغ الكلي" : "Total"}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "المتبقي" : "Remaining"}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "تاريخ الاستحقاق" : "Due Date"}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "الحالة" : "Status"}</TableHead>
                    <TableHead className="text-amber-500">{language === "ar" ? "إجراءات" : "Actions"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPurchases.map((purchase) => {
                    const remaining = purchase.remaining || (purchase.total - (purchase.amount_paid || 0));
                    const status = getStatusBadge(purchase.due_date, remaining);
                    const StatusIcon = status.icon;
                    const phone = getSupplierPhone(purchase.supplier_id);
                    
                    return (
                      <React.Fragment key={purchase.id}>
                        <TableRow 
                          className="border-slate-700 hover:bg-slate-800/50 cursor-pointer"
                          onClick={() => toggleRowExpand(purchase.id)}
                        >
                          <TableCell className="w-10">
                            {expandedRows[purchase.id] ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </TableCell>
                          <TableCell className="text-white font-medium">
                            {purchase.supplier_name}
                          </TableCell>
                          <TableCell className="text-slate-300 font-mono">
                            {phone || "-"}
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {formatCurrency(purchase.total)}
                          </TableCell>
                          <TableCell className="text-orange-500 font-bold">
                            {formatCurrency(remaining)}
                          </TableCell>
                          <TableCell className="text-slate-300">
                            {purchase.due_date 
                              ? new Date(purchase.due_date).toLocaleDateString(language === "ar" ? "ar-IQ" : "en-US")
                              : "-"}
                          </TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${status.color}`}>
                              <StatusIcon className="w-3 h-3" />
                              {status.text}
                            </span>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="sm"
                              onClick={() => sendWhatsAppReminder(purchase)}
                              className="bg-green-600 hover:bg-green-700"
                              disabled={!phone}
                            >
                              <MessageCircle className="w-4 h-4 me-1" />
                              {language === "ar" ? "تذكير" : "Remind"}
                            </Button>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expanded Details */}
                        {expandedRows[purchase.id] && (
                          <TableRow className="bg-slate-900/50 border-slate-700">
                            <TableCell colSpan={8} className="p-4">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <span className="text-slate-500">{language === "ar" ? "تاريخ الشراء:" : "Purchase Date:"}</span>
                                  <p className="text-white">{formatDateTime(purchase.created_at, language === "ar" ? "ar-IQ" : "en-US")}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">{language === "ar" ? "المدفوع:" : "Paid:"}</span>
                                  <p className="text-green-400">{formatCurrency(purchase.amount_paid || 0)}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">{language === "ar" ? "عدد المنتجات:" : "Items:"}</span>
                                  <p className="text-white">{purchase.items?.length || 0}</p>
                                </div>
                                <div>
                                  <span className="text-slate-500">{language === "ar" ? "رقم الفاتورة:" : "Invoice #:"}</span>
                                  <p className="text-white font-mono">{purchase.id?.slice(0, 8)}</p>
                                </div>
                              </div>
                              {purchase.items && purchase.items.length > 0 && (
                                <div className="mt-4">
                                  <p className="text-slate-500 mb-2">{language === "ar" ? "المنتجات:" : "Products:"}</p>
                                  <div className="flex flex-wrap gap-2">
                                    {purchase.items.map((item, idx) => (
                                      <span key={idx} className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300">
                                        {item.product_name} × {item.quantity}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
